import { Suspense, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { graphql, useLazyLoadQuery, usePaginationFragment, useMutation } from 'react-relay';
import { usePlayer } from '@/lib/PlayerContext';
import { formatDuration, formatDate } from '@/utils/format';
import { QueryErrorBoundary } from '@/components/QueryErrorBoundary';
import type { LikedEpisodesPageQuery } from '@/__generated__/LikedEpisodesPageQuery.graphql';
import type { LikedEpisodesPage_episodes$key } from '@/__generated__/LikedEpisodesPage_episodes.graphql';
import type { LikedEpisodesPageUnlikeMutation } from '@/__generated__/LikedEpisodesPageUnlikeMutation.graphql';

/**
 * Root query for LikedEpisodesPage - fetches episodes liked by the viewer
 */
const likedEpisodesPageQuery = graphql`
  query LikedEpisodesPageQuery {
    ...LikedEpisodesPage_episodes
  }
`;

/**
 * Fragment for paginated liked episodes
 */
const LikedEpisodesPage_episodes = graphql`
  fragment LikedEpisodesPage_episodes on QueryRoot
  @refetchable(queryName: "LikedEpisodesPageRefetchQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 20 }
    after: { type: "String" }
  ) {
    likedEpisodes(first: $first, after: $after)
      @connection(key: "LikedEpisodesPage_likedEpisodes") {
      edges {
        node {
          id
          topicSlug
          episodeId
          title
          summary
          audioS3Key
          durationSeconds
          publishedAt
          isLikedByViewer
          likeCount
          chapters {
            id
            title
            startTimeMs
            endTimeMs
            description
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Mutation to unlike an episode
 */
const unlikeEpisodeMutation = graphql`
  mutation LikedEpisodesPageUnlikeMutation($input: UnlikeEpisodeInput!) {
    unlikeEpisode(input: $input) {
      episode {
        id
        isLikedByViewer
        likeCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

interface EpisodeData {
  readonly id: string;
  readonly topicSlug: string;
  readonly episodeId: string;
  readonly title: string;
  readonly summary: string;
  readonly audioS3Key: string;
  readonly durationSeconds: number;
  readonly publishedAt: string;
  readonly isLikedByViewer: boolean | null | undefined;
  readonly likeCount: number;
  readonly chapters: ReadonlyArray<{
    readonly id: string;
    readonly title: string;
    readonly startTimeMs: number;
    readonly endTimeMs: number;
    readonly description: string | null | undefined;
  }>;
}

interface EpisodeItemProps {
  episode: EpisodeData;
  onPlay: () => void;
  onUnlike: () => void;
  isCurrentEpisode: boolean;
  isPlaying: boolean;
  isUnliking: boolean;
}

function EpisodeItem({ episode, onPlay, onUnlike, isCurrentEpisode, isPlaying, isUnliking }: EpisodeItemProps) {
  return (
    <div
      className={`rounded-xl border bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4 transition-all hover:border-gray-700 ${
        isCurrentEpisode ? 'border-indigo-500 ring-1 ring-indigo-500/50' : 'border-gray-800'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Play button */}
        <button
          type="button"
          onClick={onPlay}
          className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
            isPlaying
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg className="h-5 w-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Episode info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <Link
              to={`/p/${episode.topicSlug}`}
              className="font-medium text-indigo-400 hover:text-indigo-300"
            >
              {episode.topicSlug}
            </Link>
            <span>•</span>
            <span>{formatDate(episode.publishedAt)}</span>
          </div>

          <h3 className="font-semibold text-white line-clamp-2 mb-1">
            {episode.title}
          </h3>

          <p className="text-sm text-gray-400 line-clamp-2 mb-3">
            {episode.summary}
          </p>

          {/* Meta info and unlike button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>{formatDuration(episode.durationSeconds)}</span>
              {episode.likeCount > 0 && (
                <span className="flex items-center gap-1">
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
                  </svg>
                  {episode.likeCount}
                </span>
              )}
            </div>

            {/* Unlike button */}
            <button
              type="button"
              onClick={onUnlike}
              disabled={isUnliking}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUnliking ? (
                <>
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </>
              ) : (
                <>
                  <svg
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
                  </svg>
                  Unlike
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-800">
        <svg
          className="h-8 w-8 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">No liked episodes</h3>
      <p className="text-gray-400 mb-6 max-w-md mx-auto">
        You haven't liked any episodes yet. Like episodes to save them here for easy access later.
      </p>
      <Link
        to="/dashboard/discover"
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>
        Discover Topics
      </Link>
    </div>
  );
}

interface LikedEpisodesPageContentProps {
  queryRef: LikedEpisodesPage_episodes$key;
}

function LikedEpisodesPageContent({ queryRef }: LikedEpisodesPageContentProps) {
  const { playEpisode, currentEpisode, isPlaying, setIsPlaying } = usePlayer();

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    LikedEpisodesPage_episodes,
    queryRef
  );

  const [commitUnlike, isUnlikingMutation] = useMutation<LikedEpisodesPageUnlikeMutation>(unlikeEpisodeMutation);

  const episodes = useMemo(() => {
    return data.likedEpisodes.edges.map((edge) => edge.node);
  }, [data.likedEpisodes.edges]);

  const handlePlayEpisode = useCallback((episode: EpisodeData) => {
    const isCurrentEpisode = currentEpisode?.episodeId === episode.episodeId;
    if (isCurrentEpisode) {
      setIsPlaying(!isPlaying);
    } else {
      playEpisode({
        episodeId: episode.episodeId,
        topicSlug: episode.topicSlug,
        topicName: episode.topicSlug,
        title: episode.title,
        summary: episode.summary,
        audioS3Key: episode.audioS3Key,
        durationSeconds: episode.durationSeconds,
        publishedAt: episode.publishedAt,
        chapters: episode.chapters.map((ch) => ({
          id: ch.id,
          title: ch.title,
          startTimeMs: ch.startTimeMs,
          endTimeMs: ch.endTimeMs,
        })),
      });
    }
  }, [currentEpisode, isPlaying, playEpisode, setIsPlaying]);

  const handleUnlike = useCallback((episode: EpisodeData) => {
    commitUnlike({
      variables: {
        input: {
          topicSlug: episode.topicSlug,
          episodeId: episode.episodeId,
        },
      },
      updater: (store) => {
        // Remove the unliked episode from the connection
        const queryRecord = store.getRoot();
        const connection = queryRecord.getLinkedRecord('likedEpisodes', { first: 20 });
        if (connection) {
          const edges = connection.getLinkedRecords('edges');
          if (edges) {
            const newEdges = edges.filter((edge) => {
              const node = edge.getLinkedRecord('node');
              if (node) {
                return node.getValue('episodeId') !== episode.episodeId;
              }
              return true;
            });
            connection.setLinkedRecords(newEdges, 'edges');
          }
        }
      },
      onError: (error) => {
        console.error('Failed to unlike episode:', error);
      },
    });
  }, [commitUnlike]);

  if (episodes.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Liked Episodes</h2>
        <p className="mt-1 text-gray-400">
          Episodes you've liked ({data.likedEpisodes.totalCount})
        </p>
      </div>

      <div className="space-y-4">
        {episodes.map((episode) => (
          <EpisodeItem
            key={episode.id}
            episode={episode}
            onPlay={() => handlePlayEpisode(episode)}
            onUnlike={() => handleUnlike(episode)}
            isCurrentEpisode={currentEpisode?.episodeId === episode.episodeId}
            isPlaying={currentEpisode?.episodeId === episode.episodeId && isPlaying}
            isUnliking={isUnlikingMutation}
          />
        ))}
      </div>

      {hasNext && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => loadNext(20)}
            disabled={isLoadingNext}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingNext ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}
    </>
  );
}

function LikedEpisodesPageLoader() {
  const data = useLazyLoadQuery<LikedEpisodesPageQuery>(likedEpisodesPageQuery, {}, {
    fetchPolicy: 'store-and-network',
  });

  return <LikedEpisodesPageContent queryRef={data} />;
}

export function LikedEpisodesPage() {
  return (
    <QueryErrorBoundary>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        }
      >
        <LikedEpisodesPageLoader />
      </Suspense>
    </QueryErrorBoundary>
  );
}
